import { useQueryClient } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags.ts";
import { ApiStatusToFileStatus, HIDDEN_FILE_KINDS, PRIORITIZE_PAGE_CONTENT } from "@utils/constants";
import { EHR_SYNC_REFRESH_GATED_MESSAGE } from "@utils/ehrActions";
import { getOptionValueList, matchOrCreateItems } from "@utils/helpers";
import { graphqlErrorHandler, nonNullable } from "@utils/utils";
import { GraphQLError } from "graphql";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import { Headline, MultiSelect, MultiSelectItem, Paragraph, TextArea } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileFileKindChoices,
  ApiLearnerFileStatusChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ApiReportTemplateFileUploadConfigChoices,
  AssessmentToolType,
  LearnerFileType,
  ReportPageLocationEnums,
  useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation,
  useAssessmentToolsQuery,
  useCreateReportTemplateSectionsMutation,
} from "@api/graphql/types-and-hooks";
import { ContentRow } from "@components/ContentRow/ContentRow";
import { UploadDocuments } from "@components/UploadDocuments/UploadDocuments";
import { REASONS_TO_BE_OPEN, ValidationNoticeDialog } from "@components/ValidationNoticeDialog/ValidationNoticeDialog";
import { notifyError } from "@components/notifications/notifications";
import { ClinicalReportFooterButtonRow } from "@pages/ClinicalReportDetails/Components/ClinicalReportFooterButtonRow";
import { Assessments } from "@pages/ClinicalReportDetails/UploadFiles/Assessments/Assessments";
import { BHTIAFiles } from "@pages/ClinicalReportDetails/UploadFiles/BHTIAFiles";
import { BehavioralObservations } from "@pages/ClinicalReportDetails/UploadFiles/BehavioralObservations";
import { ClinicalNotes } from "@pages/ClinicalReportDetails/UploadFiles/ClinicalNotes";
import { DiagnosisReport } from "@pages/ClinicalReportDetails/UploadFiles/DiagnosisReport";
import { IntakeCalls } from "@pages/ClinicalReportDetails/UploadFiles/IntakeCall";
import { Interviews } from "@pages/ClinicalReportDetails/UploadFiles/Interviews/Interviews";
import {
  ReportClientDetails,
  ReportClientDetailsRef,
} from "@pages/ClinicalReportDetails/UploadFiles/ReportClientDetails";
import { useAdminData } from "@providers/AdminDataProvider";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";

export const ClinicalReportUploadFiles: React.FC = () => {
  const { reportType, reportSections, isDiagnosisEvaluation, isInitialAssessment, isReassessment } =
    useClinicalReportDetailsData();
  const { report, reportQuery } = useReport();
  const { isEhrSyncCleared } = useAdminData();

  // Managed-onboarding gate: the reassessment "Next" is the point that kicks off
  // the EHR programming refresh (→ ../refreshing-data). Block it for an uncleared
  // tenant — everything up to here (report creation, file upload, notes) stays
  // usable. The initial-assessment path never touches the EHR, so it's unaffected.
  const isReassessmentEhrGated = isReassessment && !isEhrSyncCleared;

  const queryClient = useQueryClient();

  const { reportId } = useParams();
  const go = useNavigate();
  const flags = useFlags<FeatureFlags>();
  const { refetch } = reportQuery;
  const formRef = useRef<ReportClientDetailsRef>(null);
  const [validationDialogOpen, setValidationDialogOpen] = React.useState<REASONS_TO_BE_OPEN | null>(null);
  const addUpdatePrioritizeMutation = useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation({});
  const createReportTemplateSections = useCreateReportTemplateSectionsMutation({});
  const [isGenerating, setIsGenerating] = useState(false);

  const isDbSinglePage = flags["db-single-page"];
  const isDbIntakeUpload = flags["db-intake-upload"];

  // Previous assessment report upload hooks (for reassessment flow)

  const fileUploadConfig = report?.reportTemplate?.fileUploadConfig;

  const isCatalightTemplate = fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();

  const isReportPageAlreadyGenerated = reportSections.some(
    (section) =>
      section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending ||
      section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
  );

  const { data: assessmentToolsData } = useAssessmentToolsQuery({ reportType });
  const supportedAssessmentsList: AssessmentToolType[] = useMemo(
    () => assessmentToolsData?.assessmentTools?.filter((item) => !!item).filter((tool) => tool.parsingSupported) ?? [],
    [assessmentToolsData],
  );
  const supportedAssessmentIds = useMemo(
    () => supportedAssessmentsList.map((tool) => tool?.id) ?? [],
    [supportedAssessmentsList],
  );

  const saveOpportunitiesForNewGoals = async (additionalUserDetails: string) => {
    try {
      await addUpdatePrioritizeMutation.mutateAsync({
        reportId: reportId ? reportId : "missing-report-id",
        prioritizeFieldsDetails: {
          additionalUserDetails: additionalUserDetails,
        },
      });
    } catch (error) {
      console.error("Error Saving Opportunities for New Goals", error);
      notifyError("Error Saving Opportunities for New Goals");
    }
  };

  const debouncedSave = useDebouncedCallback((value: string) => {
    saveOpportunitiesForNewGoals(value);
  }, 800);

  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  const handleSubmit = async () => {
    // Managed-onboarding gate (defense-in-depth; the Next button is also disabled).
    if (isReassessmentEhrGated) return;
    debouncedSave.cancel();
    // Save client details
    const isSuccess = await formRef.current?.submitForm();
    if (isSuccess) {
      await addUpdatePrioritizeMutation.mutateAsync(
        {
          reportId: reportId ? reportId : "missing-report-id",
          prioritizeFieldsDetails: {
            familyLife: reportTreatmentPlanPrioritizeFields?.familyLife ?? [],
            communicationAbility: reportTreatmentPlanPrioritizeFields?.communicationAbility ?? [],
            engagementAbility: reportTreatmentPlanPrioritizeFields?.engagementAbility ?? false,
            socialDesires: reportTreatmentPlanPrioritizeFields?.socialDesires,
            safetyConcerns: selectedSafetyConcern.length
              ? getOptionValueList(selectedSafetyConcern as unknown as MultiSelectItem[])
              : [],
            additionalUserDetails: additionalUserDetails,
          },
        },
        {
          onError: async (error) => {
            console.error("Error Saving Challenging Behaviors", error);
            notifyError("Error Saving Challenging Behaviors");
          },
        },
      );

      if (isReportPageAlreadyGenerated && isReassessment) {
        go("../refreshing-data");
        return;
      }

      try {
        const updatedFiles = (await refetch()).data?.getReports?.edges?.[0]?.node?.reportFiles.edges?.map(
          (edge) => edge?.node,
        );

        const filesForAssessment = updatedFiles?.filter((file) => file?.status !== "PENDING")?.filter(nonNullable);

        const existingAssessments =
          filesForAssessment?.filter((file) => supportedAssessmentIds.includes(file.fileKind)) ?? [];

        const parsingFiles = existingAssessments?.filter(
          (file) => file.status === ApiLearnerFileStatusChoices.Processing,
        );

        const errorFiles = existingAssessments?.filter(
          (file) => file.status === ApiLearnerFileStatusChoices.ProcessError,
        );

        // These validations only apply to Reassessment
        if (isReassessment) {
          // Require Previous Assessment Report
          const previousReportFiles = updatedFiles?.filter(
            (file) => file?.fileKind === ApiLearnerFileFileKindChoices.PreviousAssessment,
          );
          if (!previousReportFiles || previousReportFiles.length === 0) {
            setValidationDialogOpen("REASSESSMENT_MISSING_PREVIOUS_REPORT");
            return;
          }

          if (existingAssessments?.length === 0) {
            setValidationDialogOpen("REASSESSMENT_MISSING_FILES");
            return;
          }

          if (parsingFiles && parsingFiles.length > 0) {
            setValidationDialogOpen("FILES_VALIDATING");
            return;
          }

          if (errorFiles && errorFiles.length > 0) {
            setValidationDialogOpen("PARSING_FAILED");
            return;
          }
        }

        try {
          setIsGenerating(true);
          // For single-page DB view, generate report-part-1, report-part-2, and recommendations
          if (isDbSinglePage && isDiagnosisEvaluation) {
            // Kick off report-part-1
            await createReportTemplateSections.mutateAsync({
              input: {
                reportId: reportId || "<missing-report-id>",
                pageLocation: ReportPageLocationEnums.ReportPart_1,
                useConverter: false,
              },
            });

            // Wait 2 seconds before kicking off report-part-2
            await new Promise((resolve) => setTimeout(resolve, 2000));

            // Kick off report-part-2 (don't await - let it run in background)
            createReportTemplateSections
              .mutateAsync({
                input: {
                  reportId: reportId || "<missing-report-id>",
                  pageLocation: ReportPageLocationEnums.ReportPart_2,
                  useConverter: false,
                },
              })
              .catch((err) => {
                console.error("[DB Single Page] ReportPart_2 mutation failed:", err);
              });

            // Wait 2 seconds before kicking off recommendations
            await new Promise((resolve) => setTimeout(resolve, 2000));

            // Kick off recommendations (don't await - let it run in background)
            createReportTemplateSections
              .mutateAsync({
                input: {
                  reportId: reportId || "<missing-report-id>",
                  pageLocation: ReportPageLocationEnums.Recommendations,
                  useConverter: false,
                },
              })
              .catch((err) => {
                console.error("[DB Single Page] Recommendations mutation failed:", err);
              });

            queryClient.invalidateQueries({ queryKey: ["get-report-by-id", reportId] });
          } else {
            // Default behavior: only generate report-part-1
            await createReportTemplateSections
              .mutateAsync({
                input: {
                  reportId: reportId || "<missing-report-id>",
                  pageLocation: ReportPageLocationEnums.ReportPart_1,
                  useConverter: false,
                },
              })
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ["get-report-by-id", reportId] });
              });
          }

          if (isReassessment) {
            go("../refreshing-data");
          } else {
            go("../report-page");
          }
        } catch (err) {
          setIsGenerating(false);
          const errorMessages = graphqlErrorHandler(err as GraphQLError);
          for (const errorMessage of errorMessages) {
            notifyError(errorMessage);
          }
          console.error(`Failed to kickoff Report Generation`, err);
        }
      } catch (error) {
        console.error("Report file retrieval error: ", error);
        notifyError("Report file retrieval error");
      }
    }
  };

  const files = useMemo(() => report?.reportFiles.edges?.map((edge) => edge?.node).filter(nonNullable), [report]);
  const filesForAssessment = useMemo(() => {
    return files?.filter((file) => !HIDDEN_FILE_KINDS.includes(file.fileKind));
  }, [files]);
  const stillProcessing = files?.some(
    (f) =>
      f?.status === ApiLearnerFileStatusChoices.Processing ||
      f?.status === ApiLearnerFileStatusChoices.Pending ||
      f.status === ApiLearnerFileStatusChoices.Classifying,
  );

  const nextDisabledReasons = [
    isGenerating && "Please wait while report is being generated.",
    stillProcessing && "Please wait for all uploads to finish processing before continuing.",
    isDiagnosisEvaluation &&
      !filesForAssessment?.some((file) => file.status !== ApiLearnerFileStatusChoices.ProcessError) &&
      "Please upload at least one document to continue.",
    reportQuery.isFetching && "Please wait while data is loading.",
  ].filter((reason): reason is string => Boolean(reason));

  // Managed-onboarding gate: the reassessment "Next" kicks off the EHR refresh,
  // so it's disabled while the org isn't cleared. Surface the reason on the
  // Next button's own (hover) tooltip — the shared footer already wraps the
  // disabled button in a Tooltip trigger, so it's reachable there.
  let nextTooltipReason: string | undefined;
  if (isReassessmentEhrGated) {
    nextTooltipReason = `${EHR_SYNC_REFRESH_GATED_MESSAGE} Contact support to get started.`;
  } else if (nextDisabledReasons.length > 0) {
    nextTooltipReason = nextDisabledReasons.join("\n");
  }
  const filesByKind = useMemo(() => {
    const byKind: Record<string, LearnerFileType[]> = {};
    if (!filesForAssessment) return byKind;

    filesForAssessment.forEach((file) => {
      (byKind[file.fileKind] = byKind[file.fileKind] || []).push(file as LearnerFileType);
    });

    return byKind;
  }, [filesForAssessment]);

  const existingAssessments = useMemo(
    () => filesForAssessment?.filter((file) => supportedAssessmentIds.includes(file.fileKind)) ?? [],
    [filesForAssessment, supportedAssessmentIds],
  );

  const formatFileData = (file: LearnerFileType) => ({
    fileName: file.originalFileName || "<missing-file-name>",
    fileSize: file.fileSize,
    fileStatus: ApiStatusToFileStatus[file.status || ApiLearnerFileStatusChoices.Pending],
    metadata: file.metadata,
    fileKind: file.fileKind || "<missing-file-kind>",
    fileId: file.id,
    assessmentTemplateType: file.assessmentTemplateType,
    failureReason: file.processingFailureReason || "",
    failureCode: file.processingFailureCode,
  });

  const { safetyConcern } = PRIORITIZE_PAGE_CONTENT;
  const reportTreatmentPlanPrioritizeFields = report?.reportTreatmentPlanPrioritizeFields;

  const [selectedSafetyConcern, setSelectedSafetyConcern] = useState<MultiSelectItem[]>([]);
  const [additionalUserDetails, setAdditionalUserDetails] = useState<string>();

  useEffect(() => {
    if (reportTreatmentPlanPrioritizeFields?.safetyConcerns) {
      setSelectedSafetyConcern(
        matchOrCreateItems(
          reportTreatmentPlanPrioritizeFields.safetyConcerns as string[],
          safetyConcern.optionItems as MultiSelectItem[],
        ),
      );
    }
    if (reportTreatmentPlanPrioritizeFields?.additionalUserDetails) {
      setAdditionalUserDetails(reportTreatmentPlanPrioritizeFields?.additionalUserDetails);
    }
  }, [reportTreatmentPlanPrioritizeFields, safetyConcern.optionItems]);

  const clientFirstName = report?.reportClientDetails?.clientFirstName ?? "";
  const clientLastName = report?.reportClientDetails?.clientLastName ?? "";
  const learnerName = `${clientFirstName} ${clientLastName}`.trim() || undefined;
  // Put Upload Documents on top whenever Client Details are already populated — either by the
  // intake-upload flow (db-intake-upload ON) or because the user picked an existing client whose
  // details came back from the learner record. Only show Client Details first when the user
  // truly needs to fill in a sparse/new client by hand.
  //
  // Capture the order ONCE per report on the first render where report data is available,
  // then keep it stable. Otherwise autosave-triggered re-fetches would flip the order out from
  // under the user mid-typing. The ref is keyed by report.id so navigating to a different
  // report (without unmounting) re-captures fresh state instead of reusing the stale order.
  const stablePanelOrderRef = useRef<{ reportId: string; showUploadFirst: boolean } | null>(null);
  if (report?.id && stablePanelOrderRef.current?.reportId !== report.id) {
    stablePanelOrderRef.current = {
      reportId: report.id,
      showUploadFirst: isDbIntakeUpload || !!clientFirstName || !!clientLastName,
    };
  }
  const showUploadDocumentsFirst = stablePanelOrderRef.current?.showUploadFirst ?? false;

  const uploadDocumentsPanel = (
    <UploadDocuments
      supportedAssessments={supportedAssessmentsList}
      existingFiles={filesForAssessment as LearnerFileType[]}
      reportType={report?.reportType}
      fileUploadConfig={fileUploadConfig}
      reportId={report?.id}
      learnerId={report?.learner?.id}
      learnerName={learnerName}
    />
  );
  const clientDetailsPanel = <ReportClientDetails ref={formRef} />;

  return (
    <div className="flex w-full flex-col gap-y-4 p-12 pb-24">
      {/* Diagnosis Evaluation Builder Content*/}
      {isDiagnosisEvaluation &&
        (flags["db-singlepage-bulkupload"] ? (
          <div className="max-w-6xl w-full self-center flex flex-col gap-y-4">
            {showUploadDocumentsFirst ? (
              <>
                {uploadDocumentsPanel}
                {clientDetailsPanel}
              </>
            ) : (
              <>
                {clientDetailsPanel}
                {uploadDocumentsPanel}
              </>
            )}
          </div>
        ) : (
          <>
            <ReportClientDetails ref={formRef} />
            <Interviews
              existingInterviews={(filesByKind[ApiLearnerFileFileKindChoices.Interview] || []).map(formatFileData)}
            />
            <BehavioralObservations
              existingBehavioralObservations={(
                filesByKind[ApiLearnerFileFileKindChoices.BehavioralObservations] || []
              ).map(formatFileData)}
            />
            <Assessments
              existingAssessments={existingAssessments.map((file) => formatFileData(file as LearnerFileType))}
            />
            <ClinicalNotes
              existingClinicalNotes={(filesByKind[ApiLearnerFileFileKindChoices.ClinicalNotes] || []).map(
                formatFileData,
              )}
            />
          </>
        ))}

      {/* Initial Assessment Content */}
      {isInitialAssessment && (
        <>
          <ReportClientDetails ref={formRef} />
          <IntakeCalls
            existingIntakeCalls={(filesByKind[ApiLearnerFileFileKindChoices.IntakeCall] || []).map(formatFileData)}
          />

          <DiagnosisReport
            existingDiagnosis={filesByKind[ApiLearnerFileFileKindChoices.DiagnosisReport]?.map(formatFileData).pop()}
          />

          {isCatalightTemplate && (
            <BHTIAFiles
              existingBHTIAFiles={(filesByKind[ApiLearnerFileFileKindChoices.Bhtia] || []).map(formatFileData)}
            />
          )}
          <BehavioralObservations
            existingBehavioralObservations={(
              filesByKind[ApiLearnerFileFileKindChoices.BehavioralObservations] || []
            ).map(formatFileData)}
          />
          <Assessments
            existingAssessments={existingAssessments.map((file) => formatFileData(file as LearnerFileType))}
          />
          <ClinicalNotes
            existingClinicalNotes={(filesByKind[ApiLearnerFileFileKindChoices.ClinicalNotes] || []).map(formatFileData)}
          />
          <ContentRow
            title="Challenging Behaviors"
            subtitle="Let us know about any behaviors you're tracking that may need an FBA. Choose from our list of common behaviors or add your own."
          >
            <MultiSelect
              mode="tags"
              title="Challenging Behaviors"
              items={safetyConcern.optionItems as MultiSelectItem[]}
              placeholderText="Select from common behaviors or type to add your own"
              selected={selectedSafetyConcern}
              setSelected={setSelectedSafetyConcern}
              className="z-10"
            />
          </ContentRow>
        </>
      )}

      {/* Reassessment Content */}
      {isReassessment && (
        <div className="max-w-6xl w-full self-center flex flex-col gap-y-4">
          {uploadDocumentsPanel}
          {clientDetailsPanel}
          <div className="flex flex-col rounded-xl border border-border-strong bg-white p-6">
            <div className="flex flex-col mb-4">
              <Headline size="medium">Opportunities for New Goals</Headline>
              <Paragraph size="small" className="text-text-tertiary">
                Add notes about goals or behaviors not captured in uploaded assessments that you&apos;d like to address.
              </Paragraph>
            </div>
            <TextArea
              label="Areas to target"
              value={additionalUserDetails}
              onChange={(e) => {
                setAdditionalUserDetails(e.currentTarget.value);
                debouncedSave(e.currentTarget.value);
              }}
              hintText="Optional"
            ></TextArea>
            <Paragraph displayType="normal" size="small" colorType="tertiary" className="mb-2">
              <b>Examples</b>: following a morning routine, washing hands independently, sharing toys during playdates,
              using eye gaze to request items.
            </Paragraph>
          </div>
        </div>
      )}

      {/* Shared bottom content */}
      <ValidationNoticeDialog reasonToBeOpen={validationDialogOpen} closeDialog={() => setValidationDialogOpen(null)} />
      <ClinicalReportFooterButtonRow
        primaryButtonTitle={isGenerating ? "Processing..." : "Next"}
        primaryButtonDisabled={nextDisabledReasons.length > 0 || isReassessmentEhrGated}
        primaryButtonDisabledTooltipReason={nextTooltipReason}
        primaryButtonAction={handleSubmit}
        hideBackButton
        skipNavigate
        ehrSyncAlerts={report?.ehrSyncAlerts}
      />
    </div>
  );
};
